import type * as Sentry from '@sentry/react';

import { Configuration } from '@desygner/ui-core-configuration';

/**
 * User context for monitoring service
 */
export interface MonitoringUserContext {
  userId: string;
  email: string;
  workspaceDomain?: string;
}

/**
 * Generic breadcrumb data for tracking user actions
 */
export interface MonitoringBreadcrumb {
  category: string;
  message: string;
  level?: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug';
  data?: Record<string, any>;
}

export type MonitoringConfiguration = {
  environment: Configuration['environment'];
  release: Configuration['release'];
  dsn: string;
  maxBreadcrumbs?: number;
  levels: string[];
  breadcrumbs?: {
    console: boolean;
    dom: boolean;
    fetch: boolean;
    history: boolean;
    xhr: boolean;
  };
  replay?: {
    maskAllText: boolean;
    blockAllMedia: boolean;
    maskAllInputs: boolean;
    maxReplayDuration: number;
  };
  tracesSampleRate?: string;
  replaysSessionSampleRate?: string;
  sampleRate?: string;
  replaysOnErrorSampleRate?: string;
};
/**
 * Monitoring service interface for error tracking and performance monitoring
 * This service is domain-agnostic and can be used by any module
 * Implementation uses Sentry under the hood but abstracted for flexibility
 */
export interface IMonitoringService {
  /**
   * Set user context for monitoring
   * @param userContext User information
   */
  setUser(userContext: MonitoringUserContext): void;

  /**
   * Clear user context
   */
  clearUser(): void;

  /**
   * Log an exception to monitoring service
   * @param error Error to log
   * @param tags Optional tags to add to this specific error
   */
  logException(error: Error, tags?: Record<string, string | number | boolean>): void;

  /**
   * Capture an exception with additional context
   * @param error Error to capture
   * @param contextName Name of the context (e.g., 'payment', 'editor', 'upload')
   * @param contextData Context data specific to the domain
   * @param tags Optional tags for filtering
   */
  captureException(
    error: Error,
    contextName?: string,
    contextData?: Record<string, any>,
    tags?: Record<string, string | number | boolean>
  ): void;

  /**
   * Set a named context for subsequent operations
   * @param contextName Name of the context (e.g., 'payment', 'editor', 'upload')
   * @param contextData Context data
   */
  setContext(contextName: string, contextData: Record<string, any> | null): void;

  /**
   * Set tags for filtering and grouping errors
   * @param tags Key-value pairs of tags
   */
  setTags(tags: Record<string, string | number | boolean | undefined>): void;

  /**
   * Add a breadcrumb for tracking user actions
   * @param breadcrumb Breadcrumb data
   */
  addBreadcrumb(breadcrumb: MonitoringBreadcrumb): void;

  /**
   * Start a transaction for performance tracking
   * @param name Transaction name
   * @param op Operation type (e.g., 'http', 'db', 'payment', 'upload')
   * @param description Optional description
   * @returns Transaction or undefined if monitoring is not initialized
   */
  startTransaction(name: string, op: string, description?: string): Sentry.Transaction | undefined;

  setProjectContext(projectType: 'design' | 'template', projectId?: string): void;

  initialize(config: MonitoringConfiguration): void;
}

/**
 * Inversify injection type for MonitoringService
 */
export const TYPES = {
  MonitoringService: Symbol('MonitoringService'),
};

export const DEFAULT_CONFIGURATION = {
  environment: 'develop',
  release: 'develop',
  maxBreadcrumbs: 50,
  levels: ['error', 'warn'],
  breadcrumbs: {
    console: true,
    dom: true,
    fetch: true,
    history: true,
    xhr: true,
  },
  replay: {
    maskAllText: false,
    blockAllMedia: false,
    maskAllInputs: false,
    maxReplayDuration: 30 * 60 * 1000, // 30 minutes
  },
};
